import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { fetchAthleteLinescoresDetail } from "../espnRoundDetails";
import { recentHoleChipClass, scoreChipKind } from "../holeScoreChipStyles";

/** Same vs-par string as `PlayerAvatarWithScore` (tournament total from linescores). */
function tournamentScoreLine(scoreVsPar) {
    if (scoreVsPar == null || Number.isNaN(scoreVsPar)) return "—";
    if (scoreVsPar === 0) return "E";
    if (scoreVsPar > 0) return `+${scoreVsPar}`;
    return String(scoreVsPar);
}

function entrantNamesWithPick(rankings, displayName) {
    if (!rankings?.length || !displayName) return [];
    return rankings
        .filter((u) => u.picks.includes(displayName))
        .map((u) => u.name)
        .sort((a, b) => a.localeCompare(b));
}

const EMPTY_ROUND_SLOTS = ["-", "-", "-", "-"];

function HoleNineGrid({ title, slots, startIdx }) {
    const slice = slots.slice(startIdx, startIdx + 9);
    return (
        <div className="mt-4">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-masters-goldmuted">
                {title}
            </div>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-9">
                {slice.map((slot) => {
                    const label = slot.vsParLabel ?? "—";
                    const kind = scoreChipKind(label);
                    const chip = recentHoleChipClass(kind, "card");
                    return (
                        <div
                            key={slot.hole}
                            className="flex flex-col items-center gap-0.5"
                        >
                            <span className="text-[10px] tabular-nums text-masters-goldmuted">
                                {slot.hole}
                            </span>
                            <span
                                className={
                                    chip ||
                                    "text-[11px] font-medium tabular-nums text-masters-forest"
                                }
                            >
                                {label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * One mount per open player (`key={displayName}` on parent) so linescores state
 * is never stale, and layout effect + initial loading=true avoids a paint where
 * holes are empty but loading is false (“No scorecard” flash).
 */
function PlayerDetailModalContent({
    displayName,
    onClose,
    rankings,
    playerCatalog,
    eventId,
    competitionPeriod,
}) {
    const catalogEntry = playerCatalog?.[displayName] ?? null;
    const [holes, setHoles] = useState(null);
    const [roundGrossLabels, setRoundGrossLabels] = useState(EMPTY_ROUND_SLOTS);
    const [holesError, setHolesError] = useState(false);
    const [holesLoading, setHolesLoading] = useState(true);
    const [detailScores, setDetailScores] = useState(null);

    useLayoutEffect(() => {
        if (!catalogEntry?.athleteId || !eventId) {
            setHoles(null);
            setRoundGrossLabels(EMPTY_ROUND_SLOTS);
            setDetailScores(null);
            setHolesError(false);
            setHolesLoading(false);
            return undefined;
        }

        let cancelled = false;
        setHolesLoading(true);
        setHolesError(false);
        setHoles(null);
        setRoundGrossLabels(EMPTY_ROUND_SLOTS);
        setDetailScores(null);

        void (async () => {
            try {
                const detail = await fetchAthleteLinescoresDetail(
                    eventId,
                    catalogEntry.athleteId,
                    competitionPeriod,
                );
                if (cancelled) return;
                if (!detail) {
                    setHolesError(true);
                    setHoles(null);
                    setRoundGrossLabels(EMPTY_ROUND_SLOTS);
                    setDetailScores(null);
                } else {
                    setHoles(detail.holes);
                    setRoundGrossLabels(
                        detail.roundGrossLabels ?? EMPTY_ROUND_SLOTS,
                    );
                    setDetailScores({
                        tournamentVsPar: detail.tournamentVsPar,
                        currentRoundVsPar: detail.currentRoundVsPar,
                    });
                }
            } catch {
                if (!cancelled) {
                    setHolesError(true);
                    setHoles(null);
                    setRoundGrossLabels(EMPTY_ROUND_SLOTS);
                    setDetailScores(null);
                }
            } finally {
                if (!cancelled) {
                    setHolesLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        displayName,
        catalogEntry?.athleteId,
        eventId,
        competitionPeriod,
    ]);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            e.stopPropagation();
            onClose();
        };
        window.addEventListener("keydown", onKey, { capture: true });
        return () =>
            window.removeEventListener("keydown", onKey, { capture: true });
    }, [onClose]);

    const entrants = entrantNamesWithPick(rankings, displayName);
    const headerTournamentVsPar =
        detailScores === null
            ? catalogEntry?.scoreVsPar
            : (detailScores.tournamentVsPar ?? catalogEntry?.scoreVsPar);
    const headerCurrentRoundVsPar =
        detailScores === null
            ? catalogEntry?.currentRoundVsPar
            : (detailScores.currentRoundVsPar ??
              catalogEntry?.currentRoundVsPar);
    const scoreLine = catalogEntry
        ? tournamentScoreLine(headerTournamentVsPar)
        : "—";
    const todayLine = catalogEntry
        ? tournamentScoreLine(headerCurrentRoundVsPar)
        : "—";

    const hasPlayedHoleTodayOnCard =
        Array.isArray(holes) &&
        holes.some(
            (h) =>
                h.vsParLabel != null &&
                h.vsParLabel !== "" &&
                h.vsParLabel !== "—",
        );
    const catalogHasCurrentRoundVsPar =
        catalogEntry != null &&
        catalogEntry.currentRoundVsPar != null &&
        !Number.isNaN(catalogEntry.currentRoundVsPar);
    const showTodayLine =
        Boolean(catalogEntry) &&
        !holesLoading &&
        (holesError
            ? catalogHasCurrentRoundVsPar
            : Array.isArray(holes)
              ? hasPlayedHoleTodayOnCard
              : catalogHasCurrentRoundVsPar);

    const modalTree = (
        <div
            data-player-stats-overlay
            className="pointer-events-auto fixed inset-0 z-[200] flex items-end justify-center p-0 sm:items-center sm:p-4"
            role="presentation"
        >
            <button
                type="button"
                className="absolute inset-0 bg-black/50"
                aria-label="Close player details"
                onClick={onClose}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="player-detail-modal-title"
                onClick={(e) => e.stopPropagation()}
                className="relative z-[201] max-h-[min(92vh,720px)] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 text-masters-forest shadow-2xl shadow-black/40 sm:rounded-2xl"
            >
                <div className="flex items-start justify-between gap-3 border-b border-masters-parchment pb-4">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="relative shrink-0">
                            {catalogEntry?.avatarURL ? (
                                <img
                                    src={catalogEntry.avatarURL}
                                    alt=""
                                    className="h-16 w-16 rounded-full border border-masters-moss/25 object-cover"
                                />
                            ) : (
                                <div className="flex h-16 w-16 items-center justify-center rounded-full border border-dashed border-masters-moss/30 bg-masters-parchment/50 text-xs text-masters-goldmuted">
                                    —
                                </div>
                            )}
                            {catalogEntry?.flagURL ? (
                                <img
                                    src={catalogEntry.flagURL}
                                    alt={catalogEntry?.country ?? ""}
                                    className="pointer-events-none absolute bottom-0 right-0 z-[1] h-3.5 w-auto min-w-[1.125rem] max-w-[1.5rem] translate-x-0.5 translate-y-0.5 rounded-[2px] border border-gray-200 object-cover shadow-sm"
                                />
                            ) : null}
                        </div>
                        <div className="min-w-0">
                            <h2
                                id="player-detail-modal-title"
                                className="text-lg font-semibold leading-tight text-masters-forest"
                            >
                                {catalogEntry?.displayName ?? displayName}
                            </h2>
                            <div className="mt-1 text-sm text-masters-moss">
                                <span>{catalogEntry?.country ?? "—"}</span>
                            </div>
                            <p className="mt-1 text-sm font-medium text-masters-forest">
                                Current score:{" "}
                                <span className="tabular-nums">{scoreLine}</span>
                            </p>
                            {showTodayLine ? (
                                <p className="mt-0.5 text-xs text-masters-moss">
                                    Today:{" "}
                                    <span className="tabular-nums font-medium text-masters-forest">
                                        {todayLine}
                                    </span>
                                </p>
                            ) : null}
                            <div
                                className="mt-2 border-t border-masters-parchment/70 pt-2"
                                aria-label="Gross score by regulation round"
                            >
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-masters-goldmuted">
                                    Round scores (gross)
                                </div>
                                <div className="mt-1.5 grid grid-cols-4 gap-1.5 text-center sm:gap-2">
                                    {[1, 2, 3, 4].map((r) => (
                                        <div key={r}>
                                            <div className="text-[10px] text-masters-moss">
                                                R{r}
                                            </div>
                                            <div className="text-sm font-semibold tabular-nums text-masters-forest">
                                                {roundGrossLabels[r - 1] ??
                                                    "-"}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 rounded-full p-2 text-masters-moss transition hover:bg-gray-100 hover:text-masters-forest"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" strokeWidth={2} aria-hidden />
                    </button>
                </div>

                <div className="mt-4">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-masters-goldmuted">
                        Who picked this player
                    </div>
                    {entrants.length > 0 ? (
                        <ul className="mt-2 list-inside list-disc text-sm text-masters-forest">
                            {entrants.map((n) => (
                                <li key={n}>{n}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mt-2 text-sm text-masters-goldmuted">
                            No entries have this player in their picks.
                        </p>
                    )}
                </div>

                <div className="mt-2 border-t border-masters-parchment pt-4">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-masters-goldmuted">
                        Latest round (hole by hole)
                    </div>
                    {holesLoading ? (
                        <p className="mt-3 text-sm text-masters-moss">
                            Loading scorecard…
                        </p>
                    ) : holesError ? (
                        <p className="mt-3 text-sm text-masters-goldmuted">
                            Could not load hole scores.
                        </p>
                    ) : holes ? (
                        <>
                            <HoleNineGrid
                                title="Out (1–9)"
                                slots={holes}
                                startIdx={0}
                            />
                            <HoleNineGrid
                                title="In (10–18)"
                                slots={holes}
                                startIdx={9}
                            />
                        </>
                    ) : (
                        <p className="mt-3 text-sm text-masters-goldmuted">
                            No scorecard data.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modalTree, document.body);
}

export function PlayerDetailModal({
    displayName,
    onClose,
    rankings,
    playerCatalog,
    eventId,
    competitionPeriod,
}) {
    if (!displayName) return null;
    return (
        <PlayerDetailModalContent
            key={displayName}
            displayName={displayName}
            onClose={onClose}
            rankings={rankings}
            playerCatalog={playerCatalog}
            eventId={eventId}
            competitionPeriod={competitionPeriod}
        />
    );
}
